import React, { useEffect, useState } from "react";
import ContentLayout from "../components/layout/ContentLayout";
import {
  Input,
  Select,
  Form,
  InputNumber,
  Switch,
  Button,
  message,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  createProduct,
  getAllRewards,
  getProduct,
  getRewards,
  updateProduct,
  uploadProductImage,
} from "../actions/acc_actions";
import { generateRandomString } from "../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function ShopForm({ businessSlug, businessUrn }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [imageData, setImageData] = useState({ imgUrl: null, imgId: null });
  const [isUnlimited, setIsUnLimited] = useState(false);

  const { data: rewardsData } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  const { data: productData } = useQuery({
    queryKey: ["product", [id]],
    queryFn: () => getProduct(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (productData) {
      const product = productData.body;
      form.setFieldsValue({
        name_en: product.name.en,
        description_en: product.description.en,
        stock: product.is_unlimited ? 0 : product.stock,
        is_unlimited: product.is_unlimited,
        is_active: product.is_active,
        price: product.price,
        reward_type_slug: product.reward_type_slug,
        voucher_quantity: product.voucher_quantity,
      });
      setImageData({
        imgUrl: product.image?.path || null,
        imgId: product.image?.id || null,
      });
    }
  }, [productData, form]);

  const onFinish = (values) => {
    const formData = {
      ...values,
      imageId: imageData.imgId,
      name_ar: values.name_en,
      description_ar: values.description_en,
      name_en: values.name_en,
      description_en: values.description_en,
      voucher_quantity: values.voucher_quantity,
      is_unlimited: values.is_unlimited || false,
      stock: values.is_unlimited ? 0 : values.stock,
    };

    if (id) {
      updateProduct(id, formData)
        .then(() => {
          navigate(-1);
          message.success(t("product_updated_success"));
        })
        .catch(() => message.error(t("something_went_wrong")));
    } else {
      createProduct({
        ...formData,
        sku: generateRandomString(),
        business_slug: businessSlug,
        voucher_scheme_slug: businessSlug + "-vouchers",
        client_slug: "magic-stamp",
      })
        .then(() => {
          navigate(-1);
          message.success(t("product_created_success"));
        })
        .catch((err) => {
          const apiErrors = err?.response?.data?.error?.errors;

          if (Array.isArray(apiErrors)) {
            apiErrors.forEach((e) => message.error(e.msg));
          } else {
            message.error(t("something_went_wrong"));
          }
        });
    }
  };

  const onFinishFailed = () => {
    message.error(t("fill_required_fields"));
  };

  const handleUpload = ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("model", "PRODUCT");

    uploadProductImage(formData)
      .then((res) => {
        const uploadedImageUrl = res?.data?.body.url;
        const uploadedImageId = res?.data?.body.id;
        onSuccess("ok");
        setImageData({ imgUrl: uploadedImageUrl, imgId: uploadedImageId });
        message.success(t("upload") + " successful!");
      })
      .catch((error) => {
        onError(error);
        message.error(t("only_images_allowed"));
      });
  };

  return (
    <ContentLayout
      header={
        <>
          <h1>
            Add Product <span>{id ? "Update Product" : ""}</span>
          </h1>
          <p className="content_layout-subtitle">
            Fill in the product details below
          </p>
        </>
      }
    >
      <Form
        form={form}
        className="shop-form"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        {/* Rewards & Vouchers */}
        <div className="shop-form-section">
          <h5 className="active">Rewards & Vouchers</h5>
          <div className="rewards-inputs">
            <Form.Item
              name="reward_type_slug"
              label="Reward Type"
              rules={[{ required: true, message: "Please select reward type" }]}
            >
              <Select
                placeholder="Select reward type"
                options={rewardsData?.body?.map((item) => ({
                  label: item.name,
                  value: item.slug,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="voucher_quantity"
              label="Quantity"
              rules={[{ required: true, message: "Please enter quantity" }]}
            >
              <InputNumber min={1} />
            </Form.Item>
          </div>
        </div>

        {/* Basic Info */}
        <div className="shop-form-section">
          <h5 className="active">Basic Information</h5>
          <div className="rewards-inputs">
            <Form.Item
              label="Product Image"
              rules={[{ required: true, message: "Please upload an image" }]}
            >
              <Upload
                customRequest={handleUpload}
                listType="picture"
                maxCount={1}
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");
                  if (!isImage) message.error("Only images are allowed");
                  return isImage;
                }}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>

              {imageData.imgUrl && (
                <img
                  src={imageData.imgUrl}
                  alt="Uploaded"
                  style={{ marginTop: 10, maxWidth: "100px" }}
                />
              )}
            </Form.Item>

            <Form.Item
              name="name_en"
              label="Product Name"
              rules={[{ required: true, message: "Please enter product name" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description_en"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </div>
        </div>

        {/* Stock & Pricing */}
        <div className="shop-form-section">
          <h5 className="active">Stock & Pricing</h5>
          <div className="rewards-inputs">
            <div className="form-row">
              <Form.Item
                name="stock"
                label="Stock Quantity"
                rules={[
                  {
                    required: !isUnlimited,
                    message: "Please enter stock quantity",
                  },
                ]}
              >
                <InputNumber disabled={isUnlimited} min={0} />
              </Form.Item>

              <Form.Item
                name="price"
                label="Price"
                rules={[{ required: true, message: "Please enter price" }]}
              >
                <InputNumber min={0} addonAfter="$" />
              </Form.Item>

              <Form.Item
                name="is_unlimited"
                label="Unlimited Stock"
                valuePropName="checked"
              >
                <Switch onChange={(checked) => setIsUnLimited(checked)} />
              </Form.Item>

              <Form.Item
                name="is_active"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="shop-form-btn">
          <Button
            style={{
              width: "100%",
              padding: "2.4rem 0",
              fontSize: "1.8rem",
              fontWeight: "700",
            }}
            htmlType="submit"
            type="primary"
          >
            {id ? "Update Product" : "Add Product"}
          </Button>
        </div>
      </Form>
    </ContentLayout>
  );
}
